/**
 * Iframe Injector
 * Creates and configures iframe elements for embedding
 * Module 2, Section 5.2
 */

export interface IframeConfig {
  embedId: string;
  token: string;
  viewerBaseUrl: string;
  width?: string;
  height?: string;
  className?: string;
}

export class IframeInjector {
  /**
   * Create and inject iframe element
   */
  public static inject(container: HTMLElement, config: IframeConfig): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    
    // Build iframe URL to embed SPA (explicit index.html required for CloudFront)
    // Format: https://cdn.opshn.co.uk/static/embed/index.html?embedId=...&token=...&parentOrigin=...
    const url = new URL(`${config.viewerBaseUrl}/index.html`);
    url.searchParams.set('embedId', config.embedId);
    url.searchParams.set('token', config.token);
    // Forward the parent page origin so the embed SPA can send it to the API
    // for domain restriction checking. The iframe's own Referer is always
    // cdn.opshn.co.uk which would never match the embed's allowedDomains.
    url.searchParams.set('parentOrigin', window.location.origin);
    
    // Set iframe attributes
    iframe.src = url.toString();
    iframe.width = config.width || '100%';
    iframe.height = config.height || '600px';
    iframe.className = config.className || 'fashion-3d-embed';
    iframe.setAttribute('frameborder', '0');
    iframe.allowFullscreen = true;
    
    // Security: sandbox attributes
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups');
    
    // Styling
    iframe.style.border = 'none';
    iframe.style.display = 'block';
    
    // Add to container
    container.appendChild(iframe);
    
    return iframe;
  }
  
  /**
   * Create fallback content for errors
   */
  public static createFallback(container: HTMLElement, message: string): void {
    const fallback = document.createElement('div');
    fallback.className = 'fashion-3d-embed-fallback';
    fallback.innerHTML = `
      <div style="
        background: #f5f5f5;
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 40px;
        text-align: center;
        font-family: system-ui, sans-serif;
      ">
        <div style="font-size: 48px; margin-bottom: 16px;">📦</div>
        <p style="margin: 0; color: #666;">${message}</p>
      </div>
    `;
    container.appendChild(fallback);
  }
  
  /**
   * Remove iframe from container
   */
  public static remove(iframe: HTMLIFrameElement): void {
    if (iframe && iframe.parentElement) {
      iframe.parentElement.removeChild(iframe);
    }
  }
}
